import React, { useEffect, useState, useRef } from "react";
import { Input, Select, Button, Checkbox } from "antd";
import moment from "moment";
import { useIntl } from "react-intl";
import { useEmployeeCareDemands } from "../../hooks";
import EmployeesSearchWrapper from "./EmployeesSearch.style";
import EmployeesTable from "./EmployeesTable";
import { FormattedMessage } from "react-intl";
import { loadEmployeesMatching } from "../../features/scheduling/schedulingSlice";
import { useDispatch, useSelector } from "react-redux";
import useDeepCompareEffect from "use-deep-compare-effect";
const { Option } = Select;

const EmployeesSearch = (props) => {
  const {
    activeEvent,
    onChosenEmployee,
    onRemoveEmployee,
    distanceFilter,
    classificationFilter,
    type,
    originalEmployeeId,
    patient,
  } = props;
  const intl = useIntl();
  const [textFilter, setTextFilter] = useState(null);
  const [classifications, setClassifications] = useState(null);
  const [enforceLanguages, setEnforceLanguages] = useState(false);
  const [includeCandidates, setIncludeCandidates] = useState(false);

  const inputRef = useRef();

  const employeeCareDemands = useEmployeeCareDemands();
  const scopePrefix = useSelector((state) => state.user.scopePrefix);
  const { employeesMatching: matchingPage } = useSelector(
    (state) => state.scheduling
  );
  const { current, pageSize, total, order, items, filter, query } =
    matchingPage;

  const dispatch = useDispatch();

  useEffect(() => {
    if (inputRef && inputRef.current) {
      const { input } = inputRef.current;
      input.focus();
    }
  }, []);

  useEffect(() => {
    setClassifications(patient.care_demands);
  }, [patient.care_demands]);

  useDeepCompareEffect(() => {
    let effectiveFrom = null;
    let effectiveTo = null;

    // if event is recurrent add the effective date range.
    if (activeEvent.weekday != null) {
      effectiveFrom = activeEvent.order.effective_from;
      effectiveTo = activeEvent.order.effective_to;
      if (moment(effectiveFrom).isBefore(moment())) {
        effectiveFrom = moment().format();
      }
      if (moment(effectiveTo).isBefore(moment())) {
        effectiveFrom = null;
        effectiveTo = null;
      }
    }
    const params = {
      patient,
      page: {
        current: 1,
        items: [],
        filter: {
          freeText: textFilter,
          $and: [
            {
              distance_grade: {
                $gte: distanceFilter,
              },
            },
            {
              classification_grade: {
                $gte: classificationFilter,
              },
            },
            {
              care_giver: {
                $eq: true,
              },
            },
          ],
        },
        order: {
          field: "total_grade",
          order: "descend",
        },
        pageSize: 20,
        query: {
          available_from: activeEvent.start,
          available_duration: activeEvent.duration_minutes,
          classifications: classifications,
          longtitude: patient?.geo?.longtitude,
          latitude: patient?.geo?.latitude,
          effective_from: effectiveFrom,
          effective_to: effectiveTo,
          enforce_languages: enforceLanguages,
          include_candidates: includeCandidates,
          order_id: activeEvent?.order_id,
        },
      },
    };
    if (classifications) {
      dispatch(loadEmployeesMatching(params));
    }
  }, [
    textFilter,
    scopePrefix,
    classifications,
    distanceFilter,
    classificationFilter,
    activeEvent.order,
    activeEvent.start,
    activeEvent.duration_minutes,
    patient.geo,
    dispatch,
    activeEvent.weekday,
    enforceLanguages,
    includeCandidates,
  ]);

  const onTextChange = (e) => {
    setTextFilter(e.target.value);
  };

  const onClassificationsChange = (value) => {
    setClassifications(value);
  };

  const onCheckboxChange = (e) => {
    setEnforceLanguages(e.target.checked);
  };

  const onCandidatesChange = (e) => {
    setIncludeCandidates(e.target.checked);
  };

  const pagination = {
    current: current,
    pageSize: pageSize,
    total: total,
    simple: true,
  };

  const onChange = (
    { current, pageSize },
    filters,
    { field, order },
    extra
  ) => {
    let filter = {
      $and: [
        {
          distance_grade: {
            $gte: distanceFilter,
          },
        },
        {
          classification_grade: {
            $gte: classificationFilter,
          },
        },
      ],
    };
    const pageNumber = extra["action"] === "paginate" ? current : 1;
    filter["freeText"] = textFilter;
    const page = {
      current: pageNumber,
      pageSize,
      filter,
      order: {
        field,
        order,
      },
      query,
    };
    if (classifications) {
      dispatch(loadEmployeesMatching({ page, patient }));
    }
  };

  return (
    <EmployeesSearchWrapper>
      <div
        style={{
          display: "flex",
          paddingBottom: "10px",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", flex: 1 }}>
          <Input
            placeholder={intl.formatMessage({
              id: "app.employees.typeEmployeeName",
            })}
            value={textFilter}
            onChange={onTextChange}
            style={{ width: 200 }}
            allowClear
            ref={inputRef}
          />
          <div style={{ width: "10px" }} />

          <Select
            mode={"multiple"}
            allowClear
            defaultValue={patient.care_demands}
            style={{ flex: 1 }}
            placeholder={intl.formatMessage({
              id: "app.employees.addDemand",
            })}
            onChange={onClassificationsChange}
          >
            {employeeCareDemands
              ? employeeCareDemands.map((option) => (
                  <Option key={option["id"]} value={option["id"]}>
                    {option.name}
                  </Option>
                ))
              : null}
          </Select>
          <Checkbox
            style={{ margin: "0 10px", display: "flex", alignItems: "center" }}
            onChange={onCheckboxChange}
            checked={enforceLanguages}
          >
            {intl.formatMessage({
              id: "app.scheduling.enforcedLanguages",
            })}
          </Checkbox>
          <Checkbox
            style={{ margin: "0 10px", display: "flex", alignItems: "center" }}
            onChange={onCandidatesChange}
            checked={includeCandidates}
          >
            {intl.formatMessage({
              id: "app.scheduling.includeCandidates",
            })}
          </Checkbox>
        </div>
        <Button type="text" danger onClick={onRemoveEmployee}>
          {<FormattedMessage id="app.scheduling.removeEmployeeScheduling" />}
        </Button>
      </div>
      <EmployeesTable
        employees={items}
        activeEvent={activeEvent}
        onChosenEmployee={onChosenEmployee}
        pagination={pagination}
        onChange={onChange}
        type={type}
        originalEmployeeId={originalEmployeeId}
      />
    </EmployeesSearchWrapper>
  );
};

export default EmployeesSearch;
